import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, trendingArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { useState } from 'react';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [isShareOpen, setIsShareOpen] = useState(false);
  const allArticles = [...articles, ...trendingArticles];

  // Find article by slug
  const article = allArticles.find((a) => a.slug === id);

  // Find category slug for breadcrumb link
  const category = categories.find((c) => c.id === article?.categoryId || c.name === article?.categoryName);
  const categorySlug = category?.slug || 'chigua-daquan';

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章不存在</h1>
          <p className="text-foreground-500 mb-4">您访问的文章不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  // Get related articles (same category)
  const relatedArticles = allArticles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${categorySlug}` },
    { label: '文章详情' },
  ];

  // Structured data
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料吃瓜网',
      logo: {
        '@type': 'ImageObject',
        url: 'http://9288c.com/logo.png',
      },
    },
    articleSection: article.categoryName,
    keywords: article.tags?.join(', '),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://9288c.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://9288c.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `http://9288c.com/category/${categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `http://9288c.com/article/${article.slug}`,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.excerpt,
    url: `http://9288c.com/article/${article.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料吃瓜网',
      url: 'http://9288c.com/',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([articleSchema, breadcrumbSchema, webPageSchema]),
        }}
      />

      <Navbar />

      <main className="pt-20 md:pt-24 pb-12" role="main">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Article Header */}
          <article className="mt-6" itemScope itemType="https://schema.org/Article">
            <meta itemProp="headline" content={article.title} />
            <meta itemProp="datePublished" content={article.publishDate} />
            <meta itemProp="author" content={article.author} />
            <meta itemProp="articleSection" content={article.categoryName} />

            {/* Category & Tags */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${categorySlug}`}
                className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full hover:bg-primary-100 transition-colors"
              >
                {article.categoryName}
              </Link>
              {article.isExclusive && (
                <span className="px-2 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">
                  独家
                </span>
              )}
              {article.isHot && (
                <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  热门
                </span>
              )}
              {article.tags?.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-background-100 text-foreground-500 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex items-center gap-4 mb-6 flex-wrap text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <div className="w-6 h-6 bg-secondary-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-secondary-600 text-xs" />
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line text-xs" />
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-xs" />
                {article.views.toLocaleString()} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-message-3-line text-xs" />
                {article.comments || 0} 评论
              </span>
            </div>

            {/* Featured Image */}
            <div className="relative rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover object-top"
                itemProp="image"
              />
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-foreground-600 leading-relaxed mb-6 font-medium">
                {article.excerpt}
              </p>
              <div className="text-foreground-700 leading-relaxed whitespace-pre-line text-base md:text-lg">
                {article.content?.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Share & Actions */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <div className="relative">
                    <button
                      onClick={() => setIsShareOpen(!isShareOpen)}
                      className="w-9 h-9 bg-background-100 hover:bg-primary-50 rounded-lg flex items-center justify-center text-foreground-500 hover:text-primary-600 transition-colors"
                      aria-label="分享"
                    >
                      <i className="ri-share-line" />
                    </button>
                    {isShareOpen && (
                      <div className="absolute top-full left-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-lg p-2 flex items-center gap-2 z-10 animate-in fade-in duration-200">
                        <button className="w-8 h-8 bg-[#E6162D] rounded-lg flex items-center justify-center text-background-50 hover:opacity-80 transition-opacity" aria-label="微博">
                          <i className="ri-weibo-line" />
                        </button>
                        <button className="w-8 h-8 bg-[#07C160] rounded-lg flex items-center justify-center text-background-50 hover:opacity-80 transition-opacity" aria-label="微信">
                          <i className="ri-wechat-line" />
                        </button>
                        <button className="w-8 h-8 bg-[#000000] rounded-lg flex items-center justify-center text-background-50 hover:opacity-80 transition-opacity" aria-label="抖音">
                          <i className="ri-tiktok-line" />
                        </button>
                        <button className="w-8 h-8 bg-background-100 rounded-lg flex items-center justify-center text-foreground-500 hover:bg-background-200 transition-colors" aria-label="复制链接">
                          <i className="ri-link" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-1.5 px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-600 rounded-lg text-sm font-medium transition-colors">
                    <i className="ri-thumb-up-line" />
                    点赞 {article.likes}
                  </button>
                  <button className="flex items-center gap-1.5 px-4 py-2 bg-background-100 hover:bg-background-200 text-foreground-600 rounded-lg text-sm font-medium transition-colors">
                    <i className="ri-bookmark-line" />
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 md:mt-16" aria-labelledby="related-heading">
              <h2
                id="related-heading"
                className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6 flex items-center gap-2"
              >
                <i className="ri-article-line text-primary-500" />
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} variant="horizontal" showCategory={false} />
                ))}
              </div>
            </section>
          )}

          {/* Comment Section (Mock) */}
          <section className="mt-12 md:mt-16" aria-labelledby="comments-heading">
            <h2
              id="comments-heading"
              className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6 flex items-center gap-2"
            >
              <i className="ri-message-3-line text-primary-500" />
              网友评论
              <span className="text-sm font-normal text-foreground-400">({article.comments || 0})</span>
            </h2>

            {/* Comment Form */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5 mb-6">
              <textarea
                placeholder="发表您的看法..."
                maxLength={500}
                className="w-full h-24 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-foreground-400">最多500字</span>
                <button className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors">
                  发表评论
                </button>
              </div>
            </div>

            {/* Mock Comments */}
            <div className="flex flex-col gap-4">
              {[
                {
                  user: '吃瓜群众001',
                  time: '2小时前',
                  content: '这也太劲爆了吧，没想到TA居然是这样的，果然人不可貌相啊！',
                  likes: 234,
                },
                {
                  user: '理性看客',
                  time: '5小时前',
                  content: '先让子弹飞一会儿吧，现在这些瓜真真假假太多了，还是要保持理性。',
                  likes: 156,
                },
                {
                  user: '八卦小能手',
                  time: '昨天',
                  content: '之前就听说TA私生活不太行，现在看来传闻是真的，果然无风不起浪。',
                  likes: 89,
                },
              ].map((comment, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-primary-600 text-sm" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground-800">
                        {comment.user}
                      </span>
                      <span className="text-xs text-foreground-400 ml-2">{comment.time}</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed pl-11">
                    {comment.content}
                  </p>
                  <div className="flex items-center gap-4 mt-3 pl-11">
                    <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors">
                      <i className="ri-thumb-up-line" />
                      {comment.likes}
                    </button>
                    <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors">
                      <i className="ri-message-2-line" />
                      回复
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
